import { Component, OnDestroy, OnInit } from '@angular/core';
import { JhiEventManager, JhiLanguageService } from '../../../shared/core-utils';
import { PropertyService } from '../../../shared/ontology/service/property.service';
import { PropertyDetails } from '../../../shared/ontology/model/property-details';
import { PropertySearchRequest } from '../../../shared/ontology/model/property-search-request.model';
import { SubscriptionsManager } from '../../../shared/util/subscriptions-manager';
import { ColumnFilterComponent, FilterType } from '../../../shared/column-filter/column-filter.component';
import { ColumnFilterTransitionEventModel } from '../../../shared/column-filter/column-filter-transition-event.model';
import { MatchType } from '../../../shared/column-filter/column-filter-text-with-match-options-component';
import { SORT_PREDICATE_NONE } from '../../../germplasm-manager/germplasm-search-resolve-paging-params';
import { finalize } from 'rxjs/internal/operators/finalize';
import { HttpResponse } from '@angular/common/http';
import { DropdownOptionData } from '../../../shared/column-filter/column-filter-dropdown-option-data';
import { ActivatedRoute, Router } from '@angular/router';
import { OntologyService } from '../../../shared/ontology/service/ontology.service';

@Component({
    selector: 'jhi-properties-pane',
    templateUrl: 'properties-pane.component.html'
})
export class PropertiesPaneComponent implements OnInit, OnDestroy {
    static readonly COLUMN_FILTER_EVENT_NAME = 'searchColumnFiltersChanged';
    protected readonly NAME_TEXT_LIMIT = 200;
    protected readonly DESCRIPTION_TEXT_LIMIT = 1024;

    COLUMN_FILTER_EVENT_NAME = PropertiesPaneComponent.COLUMN_FILTER_EVENT_NAME;
    itemsPerPage = 20;
    ColumnLabels = ColumnLabels;
    properties: PropertyDetails[];
    searchRequest: PropertySearchRequest;
    subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();
    totalItems: number;
    page: number;
    previousPage: number;
    predicate: any;
    reverse: boolean;
    isLoading: boolean;
    propertyFilters: any;
    protected readonly TRUNCATE_LENGTH_DEFAULT = 50;

    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private jhiLanguageService: JhiLanguageService,
                private eventManager: JhiEventManager,
                private propertyService: PropertyService,
                private ontologyService: OntologyService) {
        this.page = 1;
        this.totalItems = 0;
        this.predicate = ColumnLabels.NAME;
        this.reverse = true;
        this.searchRequest = new PropertySearchRequest();
    }

    get filters() {
        return this.propertyFilters;
    }

    set filters(filters) {
        this.propertyFilters = filters;
    }

    get request() {
        return this.searchRequest;
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.filters = this.getInitialFilters();
        this.registerColumnFiltersChanged();
        this.registerEvents();
        this.loadAll(this.request);
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.loadAll(this.request);
    }

    registerEvents(): any {
        this.subscriptionsManager.add = this.eventManager.subscribe('propertyPaneViewChanged', (event) => {
            this.loadAll(this.request);
        });
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    transition() {
        this.router.navigate(['./'], {
            queryParamsHandling: 'merge',
            queryParams: {
                page: this.page,
                size: this.itemsPerPage,
                sort: this.getSort()
            },
            relativeTo: this.activatedRoute
        });
        this.loadAll(this.request);
    }

    getSort() {
        if (this.predicate === SORT_PREDICATE_NONE) {
            return '';
        }
        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    loadAll(request: PropertySearchRequest) {
        this.isLoading = true;
        this.propertyService.searchProperties(request,
            {
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            }
        ).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<PropertyDetails[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.transition();
    }

    trackId(index: number, item: PropertyDetails) {
        return item.id;
    }

    private registerColumnFiltersChanged() {
        this.subscriptionsManager.add = this.eventManager.subscribe(PropertiesPaneComponent.COLUMN_FILTER_EVENT_NAME, (event: ColumnFilterTransitionEventModel) => {
            this.resetTable();
        });
    }

    private getInitialFilters() {
        const me = this;
        return [
            {
                key: 'nameFilter', name: 'column-filter.property.name', placeholder: 'column-filter.search.text.placeholder', type: FilterType.TEXT_WITH_MATCH_OPTIONS,
                matchType: MatchType.STARTSWITH, default: true
            },
            {
                key: 'description', name: 'column-filter.description', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT
            },
            {
                key: 'classes', name: 'column-filter.classes', type: FilterType.DROPDOWN, values: this.getClassesOptions(), multipleSelect: true,
                transform(req) {
                    ColumnFilterComponent.transformDropdownFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetDropdownFilter(this, req);
                },
            }
        ];
    }

    private async getClassesOptions(): Promise<DropdownOptionData[]> {
        const classes = await this.ontologyService.getClasses().toPromise();
        return classes.map((value: string) => {
            return {
                id: value,
                text: value
            };
        });
    }

    private clearSort() {
        this.predicate = ColumnLabels.NAME;
        this.reverse = true;
    }

    private onSuccess(data, headers) {
        this.totalItems = headers.get('X-Total-Count');
        this.properties = data;
    }
}

export enum ColumnLabels {
    'NAME' = 'propertyName',
    'DESCRIPTION' = 'propertyDefinition',
    'CLASSES' = 'classes'
}
